import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { startOfDay } from "date-fns";
import { PeriodType } from "../../../common/data/service-proxies";
import { useIdlingTimeByDuty } from "../hooks/useIdleReportsHooks";
import { IdleReportsHookParams } from "../hooks/useIdleReportsHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import { formatTooltipAxisWithUnit } from "../fuel-economy-report/chartUtils";
import ReactECharts from "echarts-for-react";
import "../fuel-economy-report/FuelEconomyReport.css";

const buildDayLabels = (
  from: Date,
  to: Date,
  periodType: PeriodType,
): string[] => {
  const result: string[] = [];
  const fromStart = startOfDay(from);
  const toStart = startOfDay(to);
  const oneDay = 24 * 60 * 60 * 1000;

  if (periodType === PeriodType.Weekly) {
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(
        new Date(d).toLocaleDateString("en-US", { weekday: "short" }),
      );
    }
  } else {
    const days: Date[] = [];
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      days.push(new Date(d));
    }
    const byWeek: { [bucket: string]: number } = {};
    days.forEach((d) => {
      const dayOfMonth = d.getDate();
      const bucket = `${Math.floor((dayOfMonth - 1) / 7) + 1}`;
      byWeek[bucket] = 1;
    });
    Object.keys(byWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((b) => result.push(`W${b}`));
  }

  return result;
};

/** Backend returns time in seconds; convert to minutes for display. */
const secondsToMinutes = (values: number[] | null | undefined): number[] =>
  (values ?? []).map((v) => v / 60);

export const IdleTimeSpentWidget: React.FC<IdleReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useIdlingTimeByDuty({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const chartOption = useMemo(() => {
    if (!data) return null;
    const labels = buildDayLabels(
      fromDate,
      toDate,
      periodType ?? PeriodType.Weekly,
    );
    const onDutyRaw = data.onDutyValues ?? [];
    const offDutyRaw = data.offDutyValues ?? [];
    const len = labels.length;
    if (len === 0) return null;
    const displayLabels = labels;
    const onDutyConverted = secondsToMinutes(onDutyRaw);
    const offDutyConverted = secondsToMinutes(offDutyRaw);
    const onDutyValues = Array.from({ length: len }, (_, i) =>
      i < onDutyConverted.length ? onDutyConverted[i] : 0,
    );
    const offDutyValues = Array.from({ length: len }, (_, i) =>
      i < offDutyConverted.length ? offDutyConverted[i] : 0,
    );
    const hasData =
      onDutyValues.some((v) => v > 0) || offDutyValues.some((v) => v > 0);
    if (!hasData) return null;

    const maxVal = Math.max(...onDutyValues, ...offDutyValues, 30);
    const yMax = Math.ceil(maxVal / 30) * 30 || 150;

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: unknown) =>
          formatTooltipAxisWithUnit(params, "min"),
      },
      legend: {
        data: [
          translate("idleReport.onDuty", { _: "On duty" }),
          translate("idleReport.offDuty", { _: "Off duty" }),
        ],
        bottom: 0,
        textStyle: { color: theme.palette.text.primary },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: displayLabels,
        axisLabel: { color: theme.palette.text.primary },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yMax,
        axisLabel: { color: theme.palette.text.primary },
        splitLine: { lineStyle: { color: theme.palette.divider } },
      },
      series: [
        {
          name: translate("idleReport.onDuty", { _: "On duty" }),
          type: "bar",
          data: onDutyValues,
          itemStyle: { color: "#3b82f6" },
          barBorderRadius: [4, 4, 0, 0],
        },
        {
          name: translate("idleReport.offDuty", { _: "Off duty" }),
          type: "bar",
          data: offDutyValues,
          itemStyle: { color: "#22c55e" },
          barBorderRadius: [4, 4, 0, 0],
        },
      ],
    };
  }, [
    data,
    fromDate,
    toDate,
    periodType,
    translate,
    theme.palette.text.primary,
    theme.palette.divider,
  ]);

  const hasData = useMemo(() => {
    if (!data) return false;
    const on = data.onDutyValues?.length ?? 0;
    const off = data.offDutyValues?.length ?? 0;
    return on > 0 || off > 0;
  }, [data]);

  return (
    <Card className="widget" style={{ height: "100%", width: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("idleReport.idleTimeSpent", {
            _: "Idle time spent (min)",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", { _: "No data" })}
            </Typography>
          </Box>
        ) : chartOption ? (
          <Box flex={1} minHeight={300} style={{ minHeight: 0, width: "100%" }}>
            <ReactECharts
              option={chartOption}
              style={{ height: "100%", width: "100%", minHeight: 300 }}
            />
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default IdleTimeSpentWidget;
